import React, { useEffect, useRef, useState } from "react";
import useReducedMotion from "../hooks/useReducedMotion";

const MANIFEST = ["FIELD", "TYPE", "PULLED", "SAVED", "STUDIO"];

/**
 * Ingress. A counter, a shifting manifest, then a two-panel curtain that
 * splits away from the centre. Short on purpose — a loader that outstays its
 * welcome is just a delay with good typography.
 */
export default function Preloader({ onDone }) {
  const [pct, setPct] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const reduced = useReducedMotion();
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (reduced) {
      doneRef.current?.();
      return undefined;
    }

    document.body.style.overflow = "hidden";

    const start = performance.now();
    const DURATION = 1250;
    let raf = 0;

    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      // Ease-out so the count sprints then settles, like a real load.
      setPct(Math.round((1 - Math.pow(1 - t, 2.4)) * 100));
      if (t < 1) raf = requestAnimationFrame(tick);
      else setLeaving(true);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  useEffect(() => {
    if (!leaving) return undefined;
    const id = setTimeout(() => {
      document.body.style.overflow = "";
      doneRef.current?.();
    }, 950);
    return () => clearTimeout(id);
  }, [leaving]);

  if (reduced) return null;

  const stage = MANIFEST[Math.min(MANIFEST.length - 1, Math.floor((pct / 100) * MANIFEST.length))];

  return (
    <div className={`preloader${leaving ? " is-leaving" : ""}`} aria-hidden="true">
      <span className="preloader__curtain preloader__curtain--t" />
      <span className="preloader__curtain preloader__curtain--b" />

      <div className="preloader__inner">
        <div className="preloader__row">
          <span className="mono">AB APP STUDIO</span>
          <span className="mono">EST. 2025 — LONDON</span>
        </div>

        <div className="preloader__count display">
          {String(pct).padStart(3, "0")}
        </div>

        <div className="preloader__row">
          <span className="mono">LOADING / {stage}</span>
          <span className="preloader__bar">
            <span style={{ transform: `scaleX(${pct / 100})` }} />
          </span>
        </div>
      </div>
    </div>
  );
}
